/**
 * DualLineChart Component
 *
 * Displays two metrics as lines on a single shared Y-axis using Recharts LineChart.
 * Use when both metrics share the same unit/scale (e.g. GP vs GP Plan).
 *
 * Performance: Uses ChartDataContext for cached data - no individual API calls
 */

import PropTypes from 'prop-types';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useChartData } from '../../contexts/ChartDataContext';
import { formatDate, formatMetricValue, formatAxisValue } from '../../utils/formatters';
import './ChartStyles.css';

const DualLineChart = ({ chartId }) => {
  const { getChartData: getCachedChartData } = useChartData();
  const cachedData = getCachedChartData(chartId);

  const { data, loading, error } = cachedData;

  if (loading) {
    return (
      <div className="chart-container loading">
        <div className="spinner"></div>
        <p>Loading chart...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-container error">
        <p className="error-message">{error}</p>
      </div>
    );
  }

  if (!data || !data.data_points || data.data_points.length === 0) {
    return (
      <div className="chart-container no-data">
        <p>No data available</p>
      </div>
    );
  }

  const {
    chart_name,
    data_points,
    bar_metric,
    line_metric,
  } = data;

  // Use bar_metric unit as shared unit (both metrics share the same unit)
  const sharedUnit = bar_metric?.metric_unit;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{formatDate(label, 'short')}</p>
          {payload.map((entry, index) => {
            const metric = entry.dataKey === 'bar_value' ? bar_metric : line_metric;
            return (
              <p key={index} className="tooltip-value" style={{ color: entry.color }}>
                {metric.metric_name}: {formatMetricValue(entry.value, sharedUnit)}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const formatXAxis = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;

    const firstDate = new Date(data_points[0]?.date);
    const lastDate = new Date(data_points[data_points.length - 1]?.date);
    const timeSpanDays = (lastDate - firstDate) / (1000 * 60 * 60 * 24);

    if (timeSpanDays <= 90) return formatDate(dateStr, 'short');
    if (timeSpanDays <= 730) return formatDate(dateStr, 'month');
    return formatDate(dateStr, 'year');
  };

  const sortedData = [...data_points].sort((a, b) =>
    new Date(a.date) - new Date(b.date)
  );

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h3 className="chart-title">{chart_name}</h3>
        <p className="chart-subtitle">
          {bar_metric.metric_name} vs {line_metric.metric_name}
        </p>
      </div>

      <div className="chart-body">
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={sortedData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#ecf0f1" />
            <XAxis
              dataKey="date"
              tickFormatter={formatXAxis}
              stroke="#7f8c8d"
              style={{ fontSize: '12px' }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              stroke="#7f8c8d"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => formatAxisValue(value, sharedUnit)}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />

            {/* First metric line */}
            <Line
              type="monotone"
              dataKey="bar_value"
              name={bar_metric.metric_name}
              stroke="#3498db"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              connectNulls
            />

            {/* Second metric line */}
            <Line
              type="monotone"
              dataKey="line_value"
              name={line_metric.metric_name}
              stroke="#e74c3c"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

DualLineChart.propTypes = {
  chartId: PropTypes.string.isRequired,
};

export default DualLineChart;
